import type { CatalogItem } from '@viva/core';
import { useEffect, useState } from 'react';

/**
 * Live TV on this device: the channels watched last, and the viewer's
 * favorites. Both keep a copy of the channel, so they open without a lookup.
 */
const RECENT_KEY = 'viva.recentChannels';
const FAVORITES_KEY = 'viva.favoriteChannels';
const MAX_RECENT = 20;
const MAX_FAVORITES = 200;

type Listener = () => void;
const listeners = new Set<Listener>();

/** Channels watched last on this source, newest first. */
export function readRecentChannels(sourceId: string, storage: Storage | undefined = safeStorage()): CatalogItem[] {
  return read(storage, RECENT_KEY).filter((item) => item.sourceId === sourceId);
}

export function readFavoriteChannels(sourceId: string, storage: Storage | undefined = safeStorage()): CatalogItem[] {
  return read(storage, FAVORITES_KEY).filter((item) => item.sourceId === sourceId);
}

export function isFavorite(item: CatalogItem, storage: Storage | undefined = safeStorage()): boolean {
  return read(storage, FAVORITES_KEY).some((entry) => sameChannel(entry, item));
}

/** Puts the channel first in "Recently watched". */
export function rememberChannel(item: CatalogItem, storage: Storage | undefined = safeStorage()): void {
  if (!storage || item.kind !== 'live') return;
  const others = read(storage, RECENT_KEY).filter((entry) => !sameChannel(entry, item));
  write(storage, RECENT_KEY, [slim(item), ...others].slice(0, MAX_RECENT));
}

/** Adds or removes a favorite; new favorites go last, so the list keeps the viewer's order. Returns the new state. */
export function toggleFavorite(item: CatalogItem, storage: Storage | undefined = safeStorage()): boolean {
  if (!storage) return false;
  const all = read(storage, FAVORITES_KEY);
  const present = all.some((entry) => sameChannel(entry, item));
  write(storage, FAVORITES_KEY, present ? all.filter((entry) => !sameChannel(entry, item)) : [...all, slim(item)].slice(-MAX_FAVORITES));
  return !present;
}

/** Recently watched or favorite channels of a source; updates when either changes. */
export function useChannelList(sourceId: string, list: 'recent' | 'favorites'): CatalogItem[] {
  const pick = list === 'recent' ? readRecentChannels : readFavoriteChannels;
  const [items, setItems] = useState(() => pick(sourceId));
  useEffect(() => {
    const update = () => setItems(pick(sourceId));
    update();
    listeners.add(update);
    return () => {
      listeners.delete(update);
    };
  }, [sourceId, pick]);
  return items;
}

/** Whether this channel is a favorite; updates when favorites change. */
export function useFavorite(item: CatalogItem): boolean {
  const [favorite, setFavorite] = useState(() => isFavorite(item));
  useEffect(() => {
    const update = () => setFavorite(isFavorite(item));
    update();
    listeners.add(update);
    return () => {
      listeners.delete(update);
    };
  }, [item]);
  return favorite;
}

function sameChannel(a: CatalogItem, b: CatalogItem): boolean {
  return a.sourceId === b.sourceId && a.key === b.key;
}

/** A channel opened from a match carries match details that don't belong in the lists. */
function slim(item: CatalogItem): CatalogItem {
  const { sportsMatch: _match, ...data } = item.data;
  return { ...item, data };
}

function read(storage: Storage | undefined, key: string): CatalogItem[] {
  try {
    const parsed: unknown = JSON.parse(storage?.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? (parsed as CatalogItem[]).filter((item) => item && typeof item.key === 'string' && typeof item.sourceId === 'string') : [];
  } catch {
    return [];
  }
}

function write(storage: Storage, key: string, items: CatalogItem[]): void {
  try {
    storage.setItem(key, JSON.stringify(items));
  } catch {
    // Full or blocked storage: the lists just don't change.
  }
  listeners.forEach((listener) => listener());
}

function safeStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}
